import { GameConfig } from "@/config/GameConfig";

/**
 * PR11 - Speed Engine V2: the single authority for player-controlled
 * MOVEMENT speed (grid-step interval), for both Campaign and Daily.
 * Deliberately framework-independent - no Phaser, DOM, storage, or
 * network - so it is fully unit-testable in isolation and reusable by
 * any future mode (Endless included, though PR11 does not implement it).
 *
 * Deliberately NOT the same concept as Skill Score's own movement-speed
 * weighting (`src/core/scoring/maneuverSpeedWeight.ts`) - that consumes
 * this module's output (the current interpolated step interval) but is a
 * separate, differently-named computation, so the two can never be
 * confused in code or in conversation. See that module's doc for why the
 * split exists.
 *
 * Two halves:
 *  - Pure, stateless functions (`computeGrowthBoost`,
 *    `computeRushDebtBoost`, `computePressureAdjustedBaseMs`,
 *    `computeUncappedTargetStepMs`, `applySpeedCap`) - the composition
 *    formula itself, each independently testable and free of any notion
 *    of "now" or "previously".
 *  - The `SpeedEngine` class - the only STATEFUL part, owning nothing but
 *    the smooth current-interval-toward-target-interval transition. It
 *    never computes growth/debt/pressure itself; `LevelGame` computes the
 *    target (via the pure functions above) and pushes it in via
 *    `setTarget()`.
 */

// ---------------------------------------------------------------------
// Caps
// ---------------------------------------------------------------------

export interface SpeedCaps {
  /** Absolute minimum step interval (ms) outside an authorized temporary speed effect. */
  readonly normalMs: number;
  /** Absolute minimum step interval (ms) while an authorized temporary speed effect (SPEED TRIAL) is active. */
  readonly temporaryMs: number;
}

export const CAMPAIGN_SPEED_CAPS: SpeedCaps = { ...GameConfig.speedEngine.campaignCaps };
export const DAILY_SPEED_CAPS: SpeedCaps = { ...GameConfig.speedEngine.dailyCaps };
/** Endless V1's permanent cap - see `GameConfig.speedEngine.endlessCaps`'s own doc for why this is a distinct constant rather than an alias of `SPEED_TRIAL_FIXED_STEP_MS`. */
export const ENDLESS_SPEED_CAPS: SpeedCaps = { ...GameConfig.speedEngine.endlessCaps };
/** Daily V6's own, lower cap (PR25) - see `GameConfig.speedEngine.dailyV6Caps`'s own doc. Never reused for a V1-V5 Daily spec (those keep `DAILY_SPEED_CAPS` forever). */
export const DAILY_V6_SPEED_CAPS: SpeedCaps = { ...GameConfig.speedEngine.dailyV6Caps };

// ---------------------------------------------------------------------
// Growth-derived boost
// ---------------------------------------------------------------------

/**
 * `addedLength = max(0, currentLength - startingLength)`, tiered:
 * segments 1-10 worth `tier1PerSegment` each, 11-20 worth
 * `tier2PerSegment` each, 21+ worth `tier3PerSegment` each. Callers must
 * pass the snake's ACTUAL current length's delta - never a pending/not-
 * yet-applied growth amount.
 */
export function computeGrowthBoost(addedLength: number): number {
  const { tier1MaxAdded, tier1PerSegment, tier2MaxAdded, tier2PerSegment, tier3PerSegment } = GameConfig.speedEngine.growthBoost;
  const L = Math.max(0, addedLength);
  const tier1 = Math.min(L, tier1MaxAdded) * tier1PerSegment;
  const tier2Span = Math.max(0, tier2MaxAdded - tier1MaxAdded);
  const tier2 = Math.min(Math.max(L - tier1MaxAdded, 0), tier2Span) * tier2PerSegment;
  const tier3 = Math.max(L - tier2MaxAdded, 0) * tier3PerSegment;
  return tier1 + tier2 + tier3;
}

// ---------------------------------------------------------------------
// Permanent RUSH debt
// ---------------------------------------------------------------------

/** The shape of `GameConfig.speedEngine.rushDebt`/`rushDebtLegacyV1` - a named type so a versioned profile (`LevelDefinition.rushDebtProfile`) can be passed into `computeRushDebtBoost()` explicitly instead of only ever reading the module-level "current" config. */
export interface RushDebtTiers {
  readonly tier1Count: number;
  readonly tier1PerFruit: number;
  readonly tier2Count: number;
  readonly tier2PerFruit: number;
  readonly tier3PerFruit: number;
}

/**
 * Tiered by cumulative yellow (RUSH) fruit count this run: 1st-3rd worth
 * `tier1PerFruit` each, 4th-6th worth `tier2PerFruit` each, 7th+ worth
 * `tier3PerFruit` each. Never reset by SAFE/CHAOS/STARFRUIT/gate-opening
 * - `cumulativeYellowCount` only ever grows within a run, and only a
 * fresh run zeroes it (see `LevelGame.reset()`).
 *
 * `tiers` defaults to the CURRENT table (`GameConfig.speedEngine.rushDebt`)
 * - Campaign and Daily Generator V5. Daily Generator V1-V4 pass
 * `GameConfig.speedEngine.rushDebtLegacyV1` explicitly (see
 * `LevelDefinition.rushDebtProfile`/`LevelGame`'s constructor) so their
 * historical speed behavior is preserved exactly forever, independent of
 * whatever the CURRENT table is tuned to later.
 */
export function computeRushDebtBoost(cumulativeYellowCount: number, tiers: RushDebtTiers = GameConfig.speedEngine.rushDebt): number {
  const { tier1Count, tier1PerFruit, tier2Count, tier2PerFruit, tier3PerFruit } = tiers;
  const n = Math.max(0, cumulativeYellowCount);
  const t1 = Math.min(n, tier1Count) * tier1PerFruit;
  const t2 = Math.min(Math.max(n - tier1Count, 0), tier2Count) * tier2PerFruit;
  const t3 = Math.max(n - tier1Count - tier2Count, 0) * tier3PerFruit;
  return t1 + t2 + t3;
}

// ---------------------------------------------------------------------
// Daily pressure-adjusted base interval
// ---------------------------------------------------------------------

/** The subset of `SurvivalPressureProfile` this module needs - kept structural (not importing `src/levels/types.ts`) so this module has no dependency direction on level data types. */
export interface PressureProfileLike {
  readonly graceMs: number;
  readonly stageMs: number;
  readonly stepReductionMsPerStage: number;
  readonly minStepMs: number;
}

/**
 * `pressureAdjustedBaseMs = max(profile.minStepMs, baseStepMs - completedStages * profile.stepReductionMsPerStage)`,
 * where `completedStages = max(0, floor((activeElapsedMs - graceMs) / stageMs))`.
 * `activeElapsedMs` must be active (pause-excluded) time only - this
 * function has no notion of "now" itself, so it is exactly as pause-safe
 * as whatever `activeElapsedMs` the caller passes in. `profile` absent
 * (Campaign, or any Daily spec with no pressure) is a no-op: returns
 * `baseStepMs` unchanged, forever.
 */
export function computePressureAdjustedBaseMs(baseStepMs: number, profile: PressureProfileLike | undefined, activeElapsedMs: number): number {
  if (!profile) return baseStepMs;
  const completedStages = Math.max(0, Math.floor((activeElapsedMs - profile.graceMs) / profile.stageMs));
  return Math.max(profile.minStepMs, baseStepMs - completedStages * profile.stepReductionMsPerStage);
}

// ---------------------------------------------------------------------
// Daily V6's continuous pressure curve (PR25) - a DELIBERATELY different
// composition rule from `computePressureAdjustedBaseMs()` above, which
// V1-V5 keep using forever, byte-for-byte unchanged. Where the V1-V5
// ramp REDUCES the base step BEFORE growth/RUSH/persistent-boost compose
// on top of it (so pressure and player-driven speed always ADD UP), V6's
// curve instead sets a CEILING on how slow normal play may be, combined
// with the player's own already-composed speed via `Math.min` - "preserve
// the FASTER of player-derived speed and pressure" (section 10 of the PR
// description). This is why it's a wholly separate function/field
// (`LevelDefinition.pressureCurve`) rather than a new `PressureProfileLike`
// shape threaded through the existing composition.
// ---------------------------------------------------------------------

/** The subset of `DailyV6PressureCurve` (`src/daily/v6/types.ts`) this module needs - kept structural for the same reason `PressureProfileLike` is, so this module has no dependency direction on Daily's own types. */
export interface CurvePressureProfileLike {
  readonly graceMs: number;
  readonly rampMs: number;
  readonly finalTargetStepMs: number;
}

/**
 * `pressureTargetStepMs(t)` - the maximum slowness (step ms) V6 pressure
 * permits at active-time `t`: exactly `startStepMs` throughout `graceMs`
 * (no pressure at all yet), then a SMOOTH LINEAR interpolation down to
 * `curve.finalTargetStepMs` over the next `rampMs`, holding at
 * `finalTargetStepMs` forever after (never overshooting past it) - so a
 * sufficiently long run is eventually pulled to exactly the final target,
 * never asked to go faster than that from pressure alone. Pure and
 * pause-safe exactly like `computePressureAdjustedBaseMs()` - the caller
 * supplies already-active (pause-excluded) `activeElapsedMs`.
 */
export function computeCurvePressureTargetStepMs(curve: CurvePressureProfileLike, startStepMs: number, activeElapsedMs: number): number {
  if (activeElapsedMs <= curve.graceMs) return startStepMs;
  const rampElapsedMs = activeElapsedMs - curve.graceMs;
  if (curve.rampMs <= 0 || rampElapsedMs >= curve.rampMs) return curve.finalTargetStepMs;
  const progress = rampElapsedMs / curve.rampMs;
  return startStepMs + (curve.finalTargetStepMs - startStepMs) * progress;
}

/**
 * `effective = max(ceilingMs, min(engineDerivedStepMs, pressureTargetStepMs(t)))`
 * - the exact formula from section 10 of the PR description. Never
 * slows a player who is already faster (a lower `engineDerivedStepMs`)
 * than what pressure currently demands - `Math.min` always keeps the
 * SMALLER (faster) of the two - and `ceilingMs` (the day's own speed
 * profile floor, `DailyV6SpeedProfile.ceilingMs`) is an absolute floor
 * regardless of how fast growth/RUSH alone would otherwise push the
 * player.
 */
export function computeV6EffectiveStepMs(engineDerivedStepMs: number, curve: CurvePressureProfileLike, startStepMs: number, activeElapsedMs: number, ceilingMs: number): number {
  const target = computeCurvePressureTargetStepMs(curve, startStepMs, activeElapsedMs);
  return Math.max(ceilingMs, Math.min(engineDerivedStepMs, target));
}

// ---------------------------------------------------------------------
// Composition
// ---------------------------------------------------------------------

export interface SpeedCompositionInput {
  /** Campaign: the world's `baseStepMs` unchanged. Daily: `computePressureAdjustedBaseMs()`'s result. */
  readonly pressureAdjustedBaseMs: number;
  readonly growthBoost: number;
  readonly rushDebtBoost: number;
  /** Daily `high_speed` (V3+) = 0.08; every other mode/day = 0. */
  readonly persistentModeBoost: number;
  /**
   * Reserved for a future percentage-based temporary modifier that
   * genuinely composes with the underlying target (added on top,
   * proportionally). SPEED TRIAL (PR11.1) deliberately does NOT use this
   * field any more - it forces an authorized FIXED interval instead (see
   * `SPEED_TRIAL_FIXED_STEP_MS`/`resolveEffectiveTargetStepMs()` below),
   * so no live caller currently passes anything but 0 here. Kept, not
   * removed, purely to preserve the architectural extensibility for
   * whatever the next genuinely-proportional temporary effect turns out
   * to be.
   */
  readonly temporarySpeedBoost: number;
}

/**
 * `uncappedTargetStepMs = pressureAdjustedBaseMs / (1 + growthBoost + rushDebtBoost + persistentModeBoost + temporarySpeedBoost)`
 * - every term is additive percentage points in ONE factor; terms are
 * never multiplied together. Not yet clamped to any cap - see
 * `applySpeedCap()`. This is the UNDERLYING composed target - what the
 * engine's real, permanent, accumulated state actually adds up to right
 * now, independent of whether an authorized fixed override (SPEED TRIAL)
 * happens to be active on top of it - see `resolveEffectiveTargetStepMs()`.
 */
export function computeUncappedTargetStepMs(input: SpeedCompositionInput): number {
  const totalBoost = input.growthBoost + input.rushDebtBoost + input.persistentModeBoost + input.temporarySpeedBoost;
  return input.pressureAdjustedBaseMs / (1 + totalBoost);
}

/**
 * `targetStepMs = max(capMs, uncappedTargetStepMs)`. Reaching the cap
 * never clamps or erases any stored growth/debt/modifier state - it only
 * clamps this one derived interval; if the uncapped value later drops
 * back below the cap (SHRINK, SPEED TRIAL ending), the next call simply
 * reflects the true underlying state again.
 */
export function applySpeedCap(uncappedStepMs: number, capMs: number): number {
  return Math.max(capMs, uncappedStepMs);
}

// ---------------------------------------------------------------------
// SPEED TRIAL's fixed movement override (PR11.1)
// ---------------------------------------------------------------------

/**
 * The exact, fixed movement interval SPEED TRIAL forces while active, in
 * both Campaign and Daily - a deliberate, named, configured value. Never
 * a percentage boost, never derived from the underlying target at the
 * moment the trial began, and never affected by Growth/RUSH debt/Daily
 * pressure/HIGH SPEED while active - see `resolveEffectiveTargetStepMs()`.
 */
export const SPEED_TRIAL_FIXED_STEP_MS: number = GameConfig.speedEngine.speedTrialFixedStepMs;

/**
 * The FINAL effective target the Speed Engine should actually transition
 * toward: an authorized fixed override (SPEED TRIAL's `fixedOverrideStepMs`,
 * already cap-clamped by the caller) when one is active, otherwise the
 * underlying composed-and-capped target. This is the single place "which
 * target wins" is decided - callers should never inline this `??`
 * themselves, so the distinction between "underlying" and "effective"
 * stays explicit and grep-able everywhere it matters.
 */
export function resolveEffectiveTargetStepMs(underlyingTargetStepMs: number, fixedOverrideStepMs: number | null): number {
  return fixedOverrideStepMs ?? underlyingTargetStepMs;
}

/**
 * Whole-number percent the FINAL (post-cap) step interval is faster than
 * `referenceBaseMs` - what HUD/toast copy shows as "SPEED +N%". Cap-aware
 * by construction: this can never claim more speed than the player is
 * actually experiencing, even when the raw composed boost is far higher
 * than what the cap allows through.
 */
export function computeEffectiveBoostPercent(referenceBaseMs: number, cappedStepMs: number): number {
  if (cappedStepMs <= 0) return 0;
  return Math.round((referenceBaseMs / cappedStepMs - 1) * 100);
}

// ---------------------------------------------------------------------
// Smooth transition (the only stateful part)
// ---------------------------------------------------------------------

/** Standard transition durations, in ms of active time. See `GameConfig.speedEngine.transitionMs` for the source values. */
export const SPEED_TRANSITION_MS = { ...GameConfig.speedEngine.transitionMs };

interface TransitionState {
  readonly fromMs: number;
  readonly toMs: number;
  elapsedMs: number;
  readonly durationMs: number;
}

/**
 * Owns the interpolated "current" step interval as it smoothly moves
 * toward a "target" step interval over a caller-specified duration.
 * Deliberately the only piece of state in this whole module - everything
 * else above is a pure function of already-known values.
 *
 * Progresses ONLY via explicit `advance(deltaMs)` calls, using
 * caller-supplied active (pause-excluded) delta - never wall-clock time,
 * never a render-frame count. This is the exact same pattern
 * `LevelGame`'s other real-time state (SPEED TRIAL/pressure) already
 * uses, so it is naturally frozen whenever the caller simply stops
 * calling `advance()` (Pause), and fully deterministic: the same
 * sequence of `setTarget()`/`advance()` calls from the same starting
 * state always produces the same `currentStepMs` at every point along
 * the way.
 */
export class SpeedEngine {
  private currentMs: number;
  private targetMs: number;
  private transition: TransitionState | null = null;

  constructor(startStepMs: number) {
    this.currentMs = startStepMs;
    this.targetMs = startStepMs;
  }

  /**
   * Sets a new target step interval `targetMs`, transitioning smoothly
   * over `durationMs` of subsequent `advance()` calls. `durationMs <= 0`
   * snaps immediately (no transition) - used for a fresh run/restart's
   * starting target, which must never inherit a transition from a
   * previous run. A `targetMs` exactly equal to the CURRENT target is a
   * no-op (nothing actually changed, so no transition is (re)started).
   * If a transition is already in flight when this is called with a
   * genuinely new target, it rebases from the CURRENT INTERPOLATED value
   * (`currentStepMs`, not the old `fromMs`) - there is never a visible
   * jump, only ever a smooth re-aim.
   */
  setTarget(targetMs: number, durationMs: number): void {
    if (targetMs === this.targetMs) return;
    this.targetMs = targetMs;
    if (durationMs <= 0) {
      this.currentMs = targetMs;
      this.transition = null;
      return;
    }
    this.transition = { fromMs: this.currentMs, toMs: targetMs, elapsedMs: 0, durationMs };
  }

  /** Advances transition progress by `deltaMs` of active time. A no-op when no transition is in flight (already at target). Clamps progress safely at completion - `elapsedMs` never exceeds `durationMs`, and `currentMs` snaps exactly to `targetMs` once reached, never overshooting past it even for a very large `deltaMs`. */
  advance(deltaMs: number): void {
    if (!this.transition) return;
    const t = this.transition;
    t.elapsedMs = Math.min(t.durationMs, t.elapsedMs + Math.max(0, deltaMs));
    const progress = t.durationMs <= 0 ? 1 : t.elapsedMs / t.durationMs;
    this.currentMs = t.fromMs + (t.toMs - t.fromMs) * progress;
    if (t.elapsedMs >= t.durationMs) {
      this.currentMs = t.toMs;
      this.transition = null;
    }
  }

  /** Immediately jumps to `startStepMs` with no transition in flight - a fresh run/restart's own starting speed, never carried over from whatever this instance was doing before. */
  reset(startStepMs: number): void {
    this.currentMs = startStepMs;
    this.targetMs = startStepMs;
    this.transition = null;
  }

  /** The interval movement ticks must actually use this instant - smoothly approaching `targetStepMs`, never jumping to it. */
  get currentStepMs(): number {
    return this.currentMs;
  }

  /** The step interval the engine is currently aiming for - what `currentStepMs` will equal once any in-flight transition completes. */
  get targetStepMs(): number {
    return this.targetMs;
  }

  get isTransitioning(): boolean {
    return this.transition !== null;
  }

  /** Milliseconds of active time left in the in-flight transition, or 0 when none is active. */
  get transitionRemainingMs(): number {
    return this.transition ? this.transition.durationMs - this.transition.elapsedMs : 0;
  }
}
